import { ShaderDataType } from "../RenderDriver/DriverDesign/RenderDevice/ShaderData";
import { Shader3D } from "../RenderEngine/RenderShader/Shader3D";
import { Component } from "../components/Component";
import { BaseRender2DType, TransformKind } from "../display/SpriteConst";
import { LayaGL } from "../layagl/LayaGL";
import { Vector2 } from "../maths/Vector2";
import { Vector4 } from "../maths/Vector4";
import { ShaderDefines2D } from "../webgl/shader/d2/ShaderDefines2D";
export var Render2DOrderMode;
(function (Render2DOrderMode) {
    Render2DOrderMode[Render2DOrderMode["elementIndex"] = 0] = "elementIndex";
    Render2DOrderMode[Render2DOrderMode["ysort"] = 1] = "ysort";
})(Render2DOrderMode || (Render2DOrderMode = {}));
export class BaseRenderNode2D extends Component {
    static initBaseRender2DCommandEncoder() {
        BaseRenderNode2D.BASERENDER2DCOLOR = Shader3D.propertyNameToID("u_baseRenderColor");
        BaseRenderNode2D.BASERENDER2DTEXTURE = Shader3D.propertyNameToID("u_baseRender2DTexture");
        BaseRenderNode2D.TILINGOFFSET = Shader3D.propertyNameToID("u_tilingOffset");
        BaseRenderNode2D.NORMAL2DTEXTURE = Shader3D.propertyNameToID("u_normal2DTexture");
        BaseRenderNode2D.NORMAL2DSTRENGTH = Shader3D.propertyNameToID("u_normal2DStrength");
        BaseRenderNode2D.SHADERDEFINE_BASERENDER2D = Shader3D.getDefineByName("BASERENDER2D");
        BaseRenderNode2D.SHADERDEFINE_LIGHT2D_ENABLE = Shader3D.getDefineByName("LIGHT2D_ENABLE");
        BaseRenderNode2D.SHADERDEFINE_LIGHT2D_EMPTY = Shader3D.getDefineByName("LIGHT2D_EMPTY");
        BaseRenderNode2D.SHADERDEFINE_LIGHT2D_ADDMODE = Shader3D.getDefineByName("LIGHT2D_SCENEMODE_ADD");
        BaseRenderNode2D.SHADERDEFINE_LIGHT2D_SUBMODE = Shader3D.getDefineByName("LIGHT2D_SCENEMODE_SUB");
        BaseRenderNode2D.SHADERDEFINE_LIGHT2D_NORMAL_PARAM = Shader3D.getDefineByName("LIGHT2D_NORMAL_PARAM");
        const commandUniform = LayaGL.renderDeviceFactory.createGlobalUniformMap("BaseRender2D");
        commandUniform.addShaderUniform(ShaderDefines2D.UNIFORM_NMATRIX_0, "u_NMatrix_0", ShaderDataType.Vector3);
        commandUniform.addShaderUniform(ShaderDefines2D.UNIFORM_NMATRIX_1, "u_NMatrix_1", ShaderDataType.Vector3);
        commandUniform.addShaderUniform(BaseRenderNode2D.BASERENDER2DCOLOR, "u_baseRenderColor", ShaderDataType.Color);
        commandUniform.addShaderUniform(BaseRenderNode2D.BASERENDER2DTEXTURE, "u_baseRender2DTexture", ShaderDataType.Texture2D);
        commandUniform.addShaderUniform(BaseRenderNode2D.TILINGOFFSET, "u_tilingOffset", ShaderDataType.Vector4);
        commandUniform.addShaderUniform(BaseRenderNode2D.NORMAL2DTEXTURE, "u_normal2DTexture", ShaderDataType.Texture2D);
        commandUniform.addShaderUniform(BaseRenderNode2D.NORMAL2DSTRENGTH, "u_normal2DStrength", ShaderDataType.Float);
        commandUniform.addShaderUniform(ShaderDefines2D.UNIFORM_CLIPMATDIR, "u_clipMatDir", ShaderDataType.Vector4);
        commandUniform.addShaderUniform(ShaderDefines2D.UNIFORM_CLIPMATPOS, "u_clipMatPos", ShaderDataType.Vector4);
    }
    static _setRenderElement2DMaterial(element, material) {
        element.subShader = material._shader.getSubShaderAt(0);
        material._setOwner2DElement(element);
        element.materialShaderData = material._shaderValues;
    }
    get renderLayer() {
        return this._renderLayer;
    }
    set renderLayer(value) {
        this._renderLayer = value;
    }
    get rect() {
        if (this._boundsChange) {
            this._boundsChange = false;
        }
        return this._rect;
    }
    get boundsChange() {
        return this._boundsChange;
    }
    set boundsChange(value) {
        this._boundsChange = value;
    }
    _getcommonUniformMap() {
        return ["BaseRender2D"];
    }
    _transformChange() {
        this.boundsChange = true;
    }
    _changeMaterialReference(lastValue, value) {
        (lastValue) && (lastValue._removeReference());
        (value) && (value._addReference());
    }
    constructor() {
        super();
        this._renderType = BaseRender2DType.baseRenderNode;
        this._renderUpdateMask = 0;
        this._layer = 0;
        this._rtsize = new Vector2();
        this._lightReceive = false;
        this._lightUpdateMark = 0;
        this._lightRecord = false;
        this._renderLayer = 1;
        this._rect = new Vector4();
        this._boundsChange = false;
        this._renderid = BaseRenderNode2D._uniqueIDCounter++;
        this._renderType = BaseRender2DType.baseRenderNode;
        this._ordingMode = Render2DOrderMode.elementIndex;
        this._renderHandle = this._createRenderHandle();
    }
    _onAdded() {
        this.owner._initShaderData();
        this.owner.renderNode2D = this;
        this._struct = this.owner._struct;
        this._spriteShaderData = this._struct.spriteShaderData;
        this.owner._struct.renderDataHandler = this._renderHandle;
        this.owner._struct.renderElements = this._renderElements;
        this.owner._struct.renderType = this._renderType;
        this.owner._updateStruct();
        this._initDefaultRenderData && this._initDefaultRenderData();
    }
    _onEnable() {
        this.owner.renderNode2D = this;
        super._onEnable();
        this.owner.globalTrans._spTransChanged(TransformKind.TRS);
        if (this._lightReceive)
            this._addRenderToLightManager();
    }
    _onDisable() {
        this.owner.renderNode2D = null;
        if (this._lightReceive)
            this._removeRenderFromLightManager();
        super._onDisable();
    }
    _onDestroy() {
        for (var i = 0, n = this._materials.length; i < n; i++) {
            let m = this._materials[i];
            m && !m.destroyed && m._removeReference();
        }
        this._renderHandle.destroy();
    }
    _getRenderHandle() {
        return this._renderHandle;
    }
    _createRenderHandle() {
        return LayaGL.render2DRenderPassFactory.create2DBaseRenderDataHandle();
    }
    _isMaterialVaild(value) {
        return true;
    }
    get layer() {
        return this._layer;
    }
    set layer(value) {
        if (this._layer !== value) {
            if (value >= 0 && value <= 30) {
                this._removeRenderFromLightManager();
                this._layer = value;
                this._addRenderToLightManager();
                this._resetUpdateMark();
            }
            else {
                throw new Error("Layer value must be 0-30.");
            }
        }
    }
    get lightReceive() {
        return this._lightReceive;
    }
    set lightReceive(value) {
        if (value === this._lightReceive)
            return;
        this._lightReceive = value;
        if (value) {
            this._addRenderToLightManager();
        }
        else {
            this._removeRenderFromLightManager();
        }
        this._renderHandle.lightReceive = value;
        this._resetUpdateMark();
    }
    _resetUpdateMark() {
        this._lightUpdateMark = 0;
    }
    _updateLight() {
        if (!this.lightReceive || !this.owner.scene || !this.owner.scene._light2DManager)
            return;
        const light2DManager = this.owner.scene._light2DManager;
        const updateMark = light2DManager._getLayerUpdateMark(this.layer);
        if (this._lightUpdateMark !== updateMark) {
            this._lightUpdateMark = updateMark;
            light2DManager._updateShaderDataByLayer(this.layer, this.owner._struct.spriteShaderData);
        }
    }
    _addRenderToLightManager() {
        if (this.owner.scene) {
            let light2DManager = this.owner.scene._light2DManager;
            if (light2DManager && !this._lightRecord) {
                light2DManager.addRender(this);
                this._lightRecord = true;
            }
        }
    }
    _removeRenderFromLightManager() {
        if (this.owner.scene) {
            const light2DManager = this.owner.scene._light2DManager;
            if (light2DManager && this._lightRecord) {
                light2DManager.removeRender(this);
                this._lightRecord = false;
            }
        }
    }
    get sharedMaterial() {
        return this._materials[0];
    }
    set sharedMaterial(value) {
        if (value && !this._isMaterialVaild(value))
            return;
        const lastValue = this._materials[0];
        if (lastValue !== value) {
            this._materials[0] = value;
            this._changeMaterialReference(lastValue, value);
            this._renderElements[0] && BaseRenderNode2D._setRenderElement2DMaterial(this._renderElements[0], value);
        }
    }
    _getRenderElements() {
        return this._renderElements;
    }
    getRenderID() {
        return this._renderid;
    }
    clear() {
        this._renderElements.length = 0;
    }
}
BaseRenderNode2D._uniqueIDCounter = 0;
